import { ExtractRouteParams } from "wouter";

import { Language, useLanguage } from "../i18n/useLanguage";
import {
  DealerCartRoute,
  DealerChassisResultsRoute,
  DealerComparePrintRoute,
  DealerContactUsRoute,
  DealerHomePageRoute,
  DealerLandingPageRoute,
  DealerPartNumberResultsRoute,
  DealerProductDetailsRoute,
  DealerProductRoute,
  DealerTiresizeResultsRoute,
  DealerVehicleResultsRoute,
  DealerVehicleResultsWithoutTrimRoute,
  DealerWishlistPrintRoute,
} from "./DealerRoutes";
import { useRouteParams } from "./useRouteParams";

export const useDealerRoutesParams = (): ParamsWithLanguage => {
  const [language] = useLanguage();

  const route = [
    useRouteParams(DealerHomePageRoute),
    useRouteParams(DealerLandingPageRoute),
    useRouteParams(DealerProductRoute),
    useRouteParams(DealerTiresizeResultsRoute),
    useRouteParams(DealerVehicleResultsWithoutTrimRoute),
    useRouteParams(DealerVehicleResultsRoute),
    useRouteParams(DealerContactUsRoute),
    useRouteParams(DealerCartRoute),
    useRouteParams(DealerWishlistPrintRoute),
    useRouteParams(DealerComparePrintRoute),
    useRouteParams(DealerProductDetailsRoute),
    useRouteParams(DealerPartNumberResultsRoute),
    useRouteParams(DealerChassisResultsRoute),
  ].find(Boolean) as Params | undefined;

  if (!route) {
    throw new Error("Unknown dealer route");
  }
  // NOTE: Route language wins over default language
  return { language, ...route } as ParamsWithLanguage;
};

type ParamsWithLanguage = Params & {
  language: Language;
};

type Params =
  | (ExtractRouteParams<typeof DealerCartRoute> & {
      productType?: never;
      searchType?: never;
      vehicleCategoryName?: never;
      vehicleManufacturerName?: never;
      vehicleModelClassId?: never;
      vehicleModelName?: never;
      vehicleSubmodel?: never;
      vehicleYear?: never;
    })
  | (ExtractRouteParams<typeof DealerChassisResultsRoute> & {
      productType?: never;
      searchType?: never;
      vehicleCategoryName?: never;
      vehicleManufacturerName?: never;
      vehicleModelClassId?: never;
      vehicleModelName?: never;
      vehicleSubmodel?: never;
      vehicleYear?: never;
    })
  | (ExtractRouteParams<typeof DealerComparePrintRoute> & {
      productType?: never;
      searchType?: never;
      vehicleCategoryName?: never;
      vehicleManufacturerName?: never;
      vehicleModelClassId?: never;
      vehicleModelName?: never;
      vehicleSubmodel?: never;
      vehicleYear?: never;
    })
  | (ExtractRouteParams<typeof DealerContactUsRoute> & {
      productType?: never;
      searchType?: never;
      vehicleCategoryName?: never;
      vehicleManufacturerName?: never;
      vehicleModelClassId?: never;
      vehicleModelName?: never;
      vehicleSubmodel?: never;
      vehicleYear?: never;
    })
  | (ExtractRouteParams<typeof DealerHomePageRoute> & {
      dealerId?: never;
      language?: never;
      productType?: never;
      searchType?: never;
      vehicleCategoryName?: never;
      vehicleManufacturerName?: never;
      vehicleModelClassId?: never;
      vehicleModelName?: never;
      vehicleSubmodel?: never;
      vehicleYear?: never;
    })
  | (ExtractRouteParams<typeof DealerLandingPageRoute> & {
      productType?: never;
      searchType?: never;
      vehicleCategoryName?: never;
      vehicleManufacturerName?: never;
      vehicleModelClassId?: never;
      vehicleModelName?: never;
      vehicleSubmodel?: never;
      vehicleYear?: never;
    })
  | (ExtractRouteParams<typeof DealerPartNumberResultsRoute> & {
      productType?: never;
      searchType?: never;
      vehicleCategoryName?: never;
      vehicleManufacturerName?: never;
      vehicleModelClassId?: never;
      vehicleModelName?: never;
      vehicleSubmodel?: never;
      vehicleYear?: never;
    })
  | (ExtractRouteParams<typeof DealerProductDetailsRoute> & {
      searchType?: never;
    })
  | (ExtractRouteParams<typeof DealerProductRoute> & {
      productType?: never;
      searchType?: never;
      vehicleCategoryName?: never;
      vehicleManufacturerName?: never;
      vehicleModelClassId?: never;
      vehicleModelName?: never;
      vehicleSubmodel?: never;
      vehicleYear?: never;
    })
  | (ExtractRouteParams<typeof DealerTiresizeResultsRoute> & {
      productType?: never;
      searchType?: never;
      vehicleCategoryName?: never;
      vehicleManufacturerName?: never;
      vehicleModelClassId?: never;
      vehicleModelName?: never;
      vehicleSubmodel?: never;
      vehicleYear?: never;
    })
  | ExtractRouteParams<typeof DealerVehicleResultsRoute>
  | (ExtractRouteParams<typeof DealerWishlistPrintRoute> & {
      productType?: never;
      searchType?: never;
      vehicleCategoryName?: never;
      vehicleManufacturerName?: never;
      vehicleModelClassId?: never;
      vehicleModelName?: never;
      vehicleSubmodel?: never;
      vehicleYear?: never;
    });
